
































































import Vue from 'vue'
import moment from 'moment'
import { TranslateResult } from 'vue-i18n'
import { PlanningAssistantPeriodicityPayload, ProjectSeriesLengthForUpdatePayload } from '@/types/payload'
import {
  PLANNING_ASSISTANT_SET_PERIODICITY,
  PLANNING_CHECK_SERIES,
  PLANNING_UPDATE_PROJECT_SERIES_LENGTH
} from '@/store/action-types'
import { EditShiftPlan } from '@/model/planning/EditShiftPlan'
import { GET_SELECTED_UI_LANGUAGE } from '@/store/getter-types'
import { AutomaticOneResponse } from '@/types/service-response'
import WarningDialog from '@/components/WarningDialog.vue'
import { SnackbarStateEnum } from '@/types/enums'

interface ComponentData {
  isUnique: boolean,
  weekDayList: Array<DayListItem>
  selectedWeekDayList: Array<number>

  repeatEveryNthWeek: number
  expireAfterRepetitions: number
  expireAfterDateIso: string | undefined

  showEditingButtons: boolean
  showWarningDialog: boolean
  showSnackbar: boolean
  snackbarState: number
  messageSnackbar: TranslateResult | string
}

interface DayListItem {
  number: number
  day: TranslateResult
}

export default Vue.extend({
  name: 'ShiftEditorPeriodicity',
  components: {
    WarningDialog
  },

  props: {
    editShift: Object as () => EditShiftPlan | undefined,
    isEdittingSerie: Boolean,
  },

  data (): ComponentData {
    return {
      isUnique: true,

      weekDayList: [
        {
          day: this.$t('planningPage.monday'),
          number: 1
        },
        {
          day: this.$t('planningPage.tuesday'),
          number: 2
        },
        {
          day: this.$t('planningPage.wednesday'),
          number: 3
        },
        {
          day: this.$t('planningPage.thursday'),
          number: 4
        },
        {
          day: this.$t('planningPage.friday'),
          number: 5
        },
        {
          day: this.$t('planningPage.saturday'),
          number: 6
        },
        {
          day: this.$t('planningPage.sunday'),
          number: 7
        },
      ],

      selectedWeekDayList: [],

      repeatEveryNthWeek: 1,
      expireAfterRepetitions: 0,
      expireAfterDateIso: undefined,

      showEditingButtons: false,
      showWarningDialog: false,
      showSnackbar: false,
      snackbarState: 0,
      messageSnackbar: ''
    }
  },

  computed: {
    expireAfterDateFormatted: {
      get (): string | undefined {
        if (this.expireAfterDateIso) {
          return moment(this.expireAfterDateIso).format('DD.MM.YYYY')
        }

        return undefined
      },

      // setter is only for clear action to work
      set (): void {
        this.expireAfterDateIso = undefined

        this.updateStoreValues()
      }
    },

    isEnabled (): boolean {
      return this.isUnique
    },

    isDisabled (): boolean {
      return !!this.editShift
    },

    selectedLocale(): string {
      return this.$store.getters[GET_SELECTED_UI_LANGUAGE].code
    },

    snackbarColor (): boolean {
      return this.snackbarState === SnackbarStateEnum.Success
    }
  },

  watch: {
    isUnique (newVal: boolean): void {
      if (newVal) {
        this.resetState()
      }

      this.updateStoreValues()
    },

    expireAfterRepetitions (newVal: any): void {
      if(this.expireAfterRepetitions) {
        if(this.expireAfterRepetitions.toString() !== this.editShift!.endAfterTimes) {
          this.showEditingButtons = true
        } else {
          this.showEditingButtons = false
        }
      } else {
        if(this.editShift!.endAfterTimes && this.editShift!.endAfterTimes !== '0') {
          this.showEditingButtons = true
        } else {
          this.showEditingButtons = false
        }
      }
    },

    expireAfterDateIso (newVal: any): void {
      if(this.expireAfterDateIso) {
        if(moment(this.expireAfterDateIso).format('YYYY-MM-DD') !== moment(this.editShift!.endAfterDate).format('YYYY-MM-DD')) {
          this.showEditingButtons = true
        } else {
          this.showEditingButtons = false
        }
      } else {
        if(this.editShift!.endAfterDate) {
          this.showEditingButtons = true
        } else {
          this.showEditingButtons = false
        }
      }
    }
  },

  methods: {
    onFieldValueChange (): void {
      this.updateStoreValues()
    },

    updateStoreValues (): void {
      const payload: PlanningAssistantPeriodicityPayload = {
        isUnique: this.isUnique,
        weekDayList: this.selectedWeekDayList,
        repeatEveryNthWeek: this.repeatEveryNthWeek,
        expireAfterRepetitions: this.expireAfterRepetitions,
        expireAfterDate: this.expireAfterDateIso && moment(this.expireAfterDateIso).toDate() || undefined
      }

      this.$store.dispatch(PLANNING_ASSISTANT_SET_PERIODICITY, payload)
    },

    resetState (): void {
      if(this.editShift) {
        let days: Array<number> = []
        if(this.editShift.monday === '1'){
          days.push(1)
        }
        if(this.editShift.tuesday === '1'){
          days.push(2)
        }
        if(this.editShift.wednesday === '1'){
          days.push(3)
        }
        if(this.editShift.thursday === '1'){
          days.push(4)
        }
        if(this.editShift.friday === '1'){
          days.push(5)
        }
        if(this.editShift.saturday === '1'){
          days.push(6)
        }
        if(this.editShift.sunday === '1'){
          days.push(7)
        }

        this.isUnique = this.editShift.unique !== '0'
        this.selectedWeekDayList = days
        this.repeatEveryNthWeek = parseInt(this.editShift.whlg)
        this.expireAfterRepetitions = this.editShift.endAfterTimes ? parseInt(this.editShift.endAfterTimes) : 0
        this.expireAfterDateIso = this.editShift.endAfterDate
      } else {
        this.isUnique = true
        this.selectedWeekDayList = []
        this.repeatEveryNthWeek = 1
        this.expireAfterRepetitions = 0
        this.expireAfterDateIso = undefined
      }
    },

    onWarningDialogClose (): void {
      this.showWarningDialog = false
    },

    onWarningDialogSave (): void {
      this.updateProjectSeriesLength()
    },

    async updateProjectSeriesLength (): Promise<void> {
      const payload: ProjectSeriesLengthForUpdatePayload = {
        ProjektSerieID: this.editShift!.projectSerieId!,
        EndAfternTimes: this.expireAfterRepetitions ? this.expireAfterRepetitions.toString() : '0',
        EndAfterDate: this.expireAfterDateIso ? this.expireAfterDateIso : null}

      try {
        const response: any = await this.$store.dispatch(PLANNING_UPDATE_PROJECT_SERIES_LENGTH, payload)
        if(response && !response.IsError && response.Data) {
          this.messageSnackbar = response.Result ? response.Result : this.$t('success.dataHaveBeenSaved').toString()
          this.snackbarState = SnackbarStateEnum.Success
        } else {
          this.messageSnackbar = response.Result ? response.Result : this.$t('errors.dataCouldNotBeSaved').toString()
          this.snackbarState = SnackbarStateEnum.Error
        }
      } catch {
        this.messageSnackbar = this.$t('errors.dataCouldNotBeSaved').toString()
        this.snackbarState = SnackbarStateEnum.Error
      } finally {
        this.showSnackbar = true
      }
    },

    onCancelButtonClick (): void {
      this.$emit('cancel')
    },

    onSaveButtonClick (): void {
      this.checkSeries()
    },

    async checkSeries (): Promise<void> {
      try {
        const response: AutomaticOneResponse = await this.$store.dispatch(PLANNING_CHECK_SERIES, this.editShift!.projectSerieId)
        if(response && !response.IsError && response.Data) {
          if(response.Data.HasDauerIST === 1) {
            this.showWarningDialog = true
          } else if(response.Data.HasDauerIST === 0){
            this.updateProjectSeriesLength()
          }
        } else {
          this.messageSnackbar = response.Result ? response.Result : this.$t('errors.dataCouldNotBeRead')
          this.snackbarState = SnackbarStateEnum.Error
        }
      } catch {
        this.messageSnackbar = this.$t('errors.dataCouldNotBeRead')
        this.snackbarState = SnackbarStateEnum.Error
      } finally {
        this.showSnackbar = true
      }
    }
  },

  mounted (): void {
    this.resetState()
  }
})
